import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import { EnvironmentVariables } from "@/modules/common/types";
import { Country } from "@/modules/common/constants";
import { AxiosResponse } from "axios";
import {
  CreateCommentRequest,
  UpdateStatusRequest,
  AssignDeveloperUserRequest,
  GetLeadTracksParams,
  GetLeadTrackActivityParams,
  GetLeadTrackStatusesParams,
  GetDeveloperUsersParams,
  GetLeadTracksResponse,
  GetLeadTrackStatusesResponse,
  GetDeveloperUsersResponse,
  LeadTrackDTO,
  LeadTrackActivityResponseDTO,
  LeadTrackCommentDTO,
  GetDeveloperResponse,
} from "../dtos";

 
export const createLeadTrack = async (
  data: {
    leadUId: string;
    developerUId?: string;
    developerUserUId?: string;
    formData?: Record<string, unknown>;
  }
): Promise<AxiosResponse<LeadTrackDTO>> => {
  const apiUrl = brandUrl<LeadTrackDTO>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-tracks`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

 
export const createLeadTracksBulk = async (
  data: {
    leadUId: string;
    developerUId: string;
    developerUserUId?: string;
    formData?: Record<string, unknown>;
  }[]
): Promise<AxiosResponse<boolean>> => {
  const apiUrl = brandUrl<boolean>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-tracks/bulk-create`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

 
export const getLeadTracks = async (
  params: GetLeadTracksParams,
): Promise<AxiosResponse<GetLeadTracksResponse>> => {
  const apiUrl = brandUrl<GetLeadTracksResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-tracks`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        page: params.page,
        pageSize: params.pageSize,
        ...(params.search && { search: params.search }),
        developerUId: params.developerUId,
        leadUId: params.leadUId,
      },
    },
  });
};

// ============================================================================
// Activity
// ============================================================================

/**
 * Get activity for a specific track (cursor-based pagination)
 * GET /v1/lead-tracks/:trackUid/activity
 */
export const getLeadTrackActivity = async (
  params: GetLeadTrackActivityParams,
): Promise<AxiosResponse<LeadTrackActivityResponseDTO>> => {
  const apiUrl = brandUrl<LeadTrackActivityResponseDTO>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-tracks/${params.trackUId}/activity`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        limit: params.limit,
        cursor: params.cursor,
        direction: params.direction,
        type: params.type,
      },
    },
  });
};

// ============================================================================
// Comments
// ============================================================================

/**
 * Create a comment on a track
 * POST /v1/lead-track-comments/:trackUid
 */
export const createTrackComment = async (
  trackUId: string,
  data: CreateCommentRequest,
): Promise<AxiosResponse<LeadTrackCommentDTO>> => {
  const apiUrl = brandUrl<LeadTrackCommentDTO>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-track-comments/${trackUId}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

/**
 * Delete (hide) a comment
 * DELETE /v1/lead-track-comments/:commentUid
 */
export const deleteTrackComment = async (
  commentUId: string,
): Promise<AxiosResponse<void>> => {
  const apiUrl = brandUrl<void>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-track-comments/${commentUId}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

// ============================================================================
// Status
// ============================================================================

/**
 * Get next status options for a track
 * GET /v1/lead-track-statuses/:leadTrackUid
 */
export const getLeadTrackStatuses = async (
  params: GetLeadTrackStatusesParams,
): Promise<AxiosResponse<GetLeadTrackStatusesResponse>> => {
  const apiUrl = brandUrl<GetLeadTrackStatusesResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-track-statuses/${params.leadTrackUId}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        page: params.page,
        pageSize: params.pageSize,
        ...(params.search && { search: params.search }),
      },
    },
  });
};

/**
 * Update a track's status
 * PATCH /v1/lead-tracks/:trackUid/status
 */
export const updateTrackStatus = async (
  trackUId: string,
  data: UpdateStatusRequest,
): Promise<AxiosResponse<LeadTrackDTO>> => {
  const apiUrl = brandUrl<LeadTrackDTO>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-tracks/${trackUId}/status`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};

// ============================================================================
// Developers
// ============================================================================

/**
 * Get developers for filter/autocomplete
 * GET /v1/developers
 */
export const fetchDeveloperOptions = async (
  searchTerm: string,
  params?: {
    activeOnLeadTracks?: boolean;
    includeCompounds?: boolean;
    country?: Country;
  },
): Promise<{ data: GetDeveloperResponse }> => {
  const apiUrl = brandUrl<GetDeveloperResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/developers`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        name: searchTerm,
        ...(params?.activeOnLeadTracks !== undefined && { activeOnLeadTracks: params.activeOnLeadTracks }),
        ...(params?.includeCompounds !== undefined && { includeCompounds: params.includeCompounds }),
        ...(params?.country && { country: params.country }),
      },
    },
  }) as Promise<{ data: GetDeveloperResponse }>;
};

// ============================================================================
// Developer Users
// ============================================================================

/**
 * Get developer users available for assignment
 * GET /v1/developers/:developerUid/users
 */
export const getDeveloperUsers = async (
  params: GetDeveloperUsersParams,
): Promise<AxiosResponse<GetDeveloperUsersResponse>> => {
  const apiUrl = brandUrl<GetDeveloperUsersResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/developers/${params.developerUId}/users`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        page: params.page,
        pageSize: params.pageSize,
        excludeBlocked: params.excludeBlocked,
        ...(params.search && { search: params.search }),
      },
    },
  });
};

/**
 * Assign/update developer user for a track
 * PATCH /v1/lead-tracks/:trackUid/developer-user
 */
export const assignDeveloperUser = async (
  trackUId: string,
  data: AssignDeveloperUserRequest,
): Promise<AxiosResponse<LeadTrackDTO>> => {
  const apiUrl = brandUrl<LeadTrackDTO>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-tracks/${trackUId}/developer-user`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};
